import { formatCountdown } from "../projects/milestoneCountdown";
import {
  PRIORITY_LABELS,
  PRIORITY_TONE,
  STATUS_LABELS,
  STATUS_TONE,
} from "../workitems/workItemVocabulary";
import type { Tone } from "../../shared/ui";
import type {
  NotificationCategory,
  NotificationContext,
  NotificationSeverity,
  NotificationView,
  Priority,
  WorkItemStatus,
} from "../../shared/types/api";

/**
 * How a notification reads (§89, §91).
 *
 * <p>Kept apart from the components so the phrasing and the arithmetic can be tested without
 * rendering anything, and so the bell and the history page cannot describe the same notification
 * two different ways.
 *
 * <p>Nothing here decides whether something is urgent or late. The server settles that — those are
 * business questions, and answering them again in the browser is how two screens end up
 * disagreeing about the same task.
 */

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * "12 Sep", with the year only when it is not this one.
 *
 * <p>Spelled out here rather than left to {@code toLocaleDateString} so that every reader sees the
 * same string. A list where one person reads 9/12/2026 and another 12/9/2026 is a list about
 * dates that cannot be discussed.
 */
export function shortDate(iso: string, now: Date = new Date()): string {
  const [year = 0, month = 1, day = 1] = iso.split("-").map(Number);
  const stem = `${day} ${MONTHS[month - 1]}`;
  return year === now.getFullYear() ? stem : `${stem} ${year}`;
}

export type DueTone = "calm" | "near" | "overdue";

export interface DueLine {
  text: string;
  tone: DueTone;
}

/** Inside this many days a date is worth colouring: near enough that plans still change. */
const NEAR_DAYS = 7;

/**
 * When the work is due, and how much of a problem that is.
 *
 * <p>The single most useful thing a notification can carry, and the reason somebody would
 * otherwise have to open the item: a task assigned to you next month and one that was due last
 * week are the same sentence until the date is on the row.
 *
 * @return null when nothing is due, which is not a failure — plenty of work has no date
 */
export function dueLine(
  context: NotificationContext | null,
  now: Date = new Date(),
): DueLine | null {
  if (!context?.dueDate || context.daysUntilDue === null) {
    return null;
  }
  const days = context.daysUntilDue;

  if (context.overdue) {
    return { text: `Due ${shortDate(context.dueDate, now)} · ${formatCountdown(days)}`,
      tone: "overdue" };
  }
  // Past its date but not overdue means the work is finished. The date is still worth showing —
  // it is why the notification exists — but there is nothing here to alarm anybody about.
  if (days < 0) {
    return { text: `Was due ${shortDate(context.dueDate, now)}`, tone: "calm" };
  }
  if (days === 0) {
    return { text: "Due today", tone: "near" };
  }
  if (days === 1) {
    return { text: "Due tomorrow", tone: "near" };
  }
  return {
    text: `Due ${shortDate(context.dueDate, now)} · ${formatCountdown(days)}`,
    tone: days <= NEAR_DAYS ? "near" : "calm",
  };
}

export interface MetaChip {
  label: string;
  tone: Tone;
}

/**
 * How urgent the work is and how far along it has got, in the application's own badges.
 *
 * <p>Anything unknown is left out rather than drawn as a dash. A row of dashes is a row that has
 * taught the reader to stop reading it.
 */
export function metaChips(context: NotificationContext | null): MetaChip[] {
  if (!context) {
    return [];
  }
  const chips: MetaChip[] = [];

  if (context.priority) {
    const priority = context.priority as Priority;
    chips.push({
      label: PRIORITY_LABELS[priority] ?? context.priority,
      tone: PRIORITY_TONE[priority] ?? "gray",
    });
  }
  if (context.status) {
    const status = context.status as WorkItemStatus;
    chips.push({
      label: STATUS_LABELS[status] ?? context.status,
      tone: STATUS_TONE[status] ?? "gray",
    });
  }
  return chips;
}

export interface DayGroup {
  label: string;
  items: NotificationView[];
}

/**
 * A history broken into days, newest first.
 *
 * <p>Fifty rows of "3 days ago" is a list nobody can find anything in. Days are the unit people
 * actually remember something happening in, and the heading does the remembering for them.
 */
export function groupByDay(items: NotificationView[], now: Date = new Date()): DayGroup[] {
  const groups: DayGroup[] = [];

  for (const item of items) {
    const label = dayLabel(item.raisedAt, now);
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.items.push(item);
    } else {
      groups.push({ label, items: [item] });
    }
  }
  return groups;
}

function dayLabel(iso: string, now: Date): string {
  const raised = new Date(iso);
  const days = calendarDaysBetween(raised, now);

  if (days === 0) {
    return "Today";
  }
  if (days === 1) {
    return "Yesterday";
  }
  return `${raised.getDate()} ${MONTHS[raised.getMonth()]} ${raised.getFullYear()}`;
}

/** Whole calendar days apart, ignoring the time of day — "yesterday" is not 24 hours ago. */
function calendarDaysBetween(then: Date, now: Date): number {
  const thenDay = Date.UTC(then.getFullYear(), then.getMonth(), then.getDate());
  const nowDay = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((nowDay - thenDay) / (24 * 60 * 60 * 1000));
}

export interface NotificationFilter {
  category: NotificationCategory | null;
  unreadOnly: boolean;
}

/** Both narrowings at once, because a reader who picks two means both. */
export function filterNotifications(
  items: NotificationView[],
  filter: NotificationFilter,
): NotificationView[] {
  return items.filter(
    (item) =>
      (filter.category === null || item.category === filter.category) &&
      (!filter.unreadOnly || !item.read),
  );
}

/**
 * The bands somebody filters by, in the order they are offered.
 *
 * <p>Not every category the server knows: the tabs are the questions people arrive with, and a
 * band nobody has notifications in is a tab that only costs them a click to discover that.
 */
export const CATEGORY_LABELS: Record<NotificationCategory, string> = {
  WORK: "My work",
  APPROVAL: "Waiting on me",
  SCHEDULE: "Dates",
  TIMESHEET: "Timesheets",
  LEAVE: "Leave",
  RELEASE: "Releases",
  PROJECT: "Projects",
  PEOPLE: "People",
  ACCOUNT: "Account",
};

export const CATEGORY_ORDER: NotificationCategory[] = [
  "WORK",
  "APPROVAL",
  "SCHEDULE",
  "TIMESHEET",
  "LEAVE",
  "RELEASE",
  "PROJECT",
  "PEOPLE",
  "ACCOUNT",
];

/** How loudly a row is drawn. The server decided this; the browser only picks the colour. */
export const SEVERITY_DOT: Record<NotificationSeverity, string> = {
  CRITICAL: "bg-red-600",
  WARNING: "bg-amber-500",
  INFO: "bg-brand-600",
};

/** The due line is the one place a row is allowed to shout, so it carries the colour itself. */
export const DUE_TONE_CLASS: Record<DueTone, string> = {
  overdue: "font-medium text-red-700",
  near: "font-medium text-amber-700",
  calm: "text-ink-600",
};

/**
 * "3 hours ago" rather than a timestamp.
 *
 * <p>A notification list is read for recency, and a wall-clock time makes the reader do the
 * subtraction. Anything older than a week gets its date back, because "14 days ago" stops being
 * easier than the date it stands for.
 */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) {
    return "";
  }
  const seconds = Math.round((now.getTime() - then) / 1000);

  if (seconds < 60) {
    return "just now";
  }
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }
  const days = Math.round(hours / 24);
  if (days <= 7) {
    return `${days} day${days === 1 ? "" : "s"} ago`;
  }
  return shortDate(new Date(then).toISOString().slice(0, 10), now);
}
